import {
  ErrorResponse,
  EventsApi,
  FindProgramEventsRequest,
  ProgramEvent,
  ProgramEventList,
} from '@citruscamps/citrus-client'
import { DateTime } from 'luxon'
import { Dispatch } from 'react'
import { FetchNextPageOptions, InfiniteQueryObserverResult } from 'react-query'
import { DefaultDetailsPagination } from '../../../../../constants/pagination'
import {
  IFetchManyProps,
  NonQueryAttributes,
  useFetchInfiniteMany,
  useFetchMany,
} from '../../../../../hooks/useFetchMany'
import {
  DateRangeType,
  FilterDateRange,
  FilterNumberRange,
} from '../../../../../interfaces/filter-range'
import { Pagination } from '../../../../../interfaces/pagination'
import { generateApiConfig } from '../../../../../utils/client-config'
import { useCreateFeedback } from './useCreateFeedback'
import { useUpdateEvents } from './useUpdateEvents'

const toDate = (date?: Date | string): Date | undefined =>
  typeof date === 'string' ? DateTime.fromISO(date).toJSDate() : date || undefined

export const DefaultEventFetchProps: EventsFetchProps = {
  sort: 'starts_at',
  order: 'ASC',
  query: {},
}

const now = DateTime.local().toJSDate()
export const getDefaultEventSearchFetchProps = (): EventsFetchProps => ({
  order: 'ASC',
  sort: 'starts_at',
  query: {
    status: ['live'],
    scheduled: {
      gt: now,
    },
    is_appointment: false,
  },
})

type AgeAttributes = `age_${keyof FilterNumberRange}`
type CapacityAttributes = `capacity_${keyof FilterNumberRange}`
type SpotsFilledAttributes = `total_registrations_${keyof FilterNumberRange}`
type ScheduledAttributes = `scheduled_${keyof FilterDateRange}`
export interface EventFilterProps
  extends Omit<
    FindProgramEventsRequest,
    | NonQueryAttributes
    | AgeAttributes
    | CapacityAttributes
    | SpotsFilledAttributes
    | ScheduledAttributes
  > {
  age?: FilterNumberRange
  capacity?: FilterNumberRange
  spots_filled?: FilterNumberRange
  scheduled?: FilterDateRange
  scheduled_type?: DateRangeType
}
export interface EventsFetchProps {
  sort?: string
  order?: 'ASC' | 'DESC'
  query: EventFilterProps
}

type IProps = Pick<
  IFetchManyProps<ProgramEvent, EventFilterProps>,
  'programId' | 'fetchProps' | 'search' | 'pagination' | 'isEnabled' | 'setFetchProps'
>

interface FetchEvents {
  data: ProgramEvent[]
  error?: ErrorResponse
  fetchProps: EventsFetchProps
  isError: boolean
  isLoading: boolean
  pagination: Pagination
  search: string
  handlePublish: (event: ProgramEvent) => Promise<void>
  handleBulkPublish: (events: ProgramEvent[], update_all?: boolean) => Promise<void>
  handleUnpublish: (event: ProgramEvent) => Promise<void>
  handleBulkUnpublish: (events: ProgramEvent[], update_all?: boolean) => Promise<void>
  handleCancel: (event: ProgramEvent) => Promise<void>
  handleBulkCancel: (events: ProgramEvent[], update_all?: boolean) => Promise<void>
  handleDelete: (event: ProgramEvent) => Promise<void>
  handleBulkDelete: (events: ProgramEvent[], update_all?: boolean) => Promise<void>
  handleCreateFeedback: (id: string) => Promise<void>
  setFetchProps: Dispatch<EventsFetchProps>
  setPagination: Dispatch<Pagination>
  setSearch: Dispatch<string>
}

export const useFetchEvents = (props: IProps): FetchEvents => {
  const {
    data,
    error,
    fetchProps,
    isError,
    isLoading,
    pagination,
    search,
    setPagination,
    setSearch,
    setFetchProps,
  } = useFetchMany<ProgramEvent, EventFilterProps>({
    type: 'program_event',
    programId: props.programId,
    fetchProps: props.fetchProps,
    search: props.search,
    defaultFetchProps: DefaultEventFetchProps,
    defaultPagination: props.pagination || DefaultDetailsPagination,
    isEnabled: props.isEnabled,
    setFetchProps: props.setFetchProps,
    queryFn: async (queryParams): Promise<ProgramEventList> => {
      const { programId, limit, skip, query, sort, order, search, signal } = queryParams
      const client = new EventsApi(generateApiConfig())
      return await client.findProgramEvents(
        {
          program_id: programId,
          limit,
          skip,
          order,
          sort,
          search,
          age_lt: query?.age?.lt,
          age_gt: query?.age?.gt,
          age_lte: query?.age?.lte,
          age_gte: query?.age?.gte,
          gender: query?.gender && query?.gender.length > 0 ? query.gender : undefined,
          venue_city:
            query?.venue_city && query?.venue_city.length > 0 ? query.venue_city : undefined,
          online_event:
            typeof query?.online_event !== 'undefined' ? query?.online_event : undefined,
          status: query?.status && query?.status.length > 0 ? query.status : undefined,
          type: query?.type && query?.type.length > 0 ? query.type : undefined,
          tags: query?.tags && query?.tags.length > 0 ? query.tags : undefined,
          id: query?.id && query?.id.length > 0 ? query.id : undefined,
          exclude_id:
            query?.exclude_id && query?.exclude_id.length > 0 ? query.exclude_id : undefined,
          is_appointment:
            typeof query?.is_appointment !== 'undefined' ? query.is_appointment : undefined,
          capacity_lt: query?.capacity?.lt,
          capacity_gt: query?.capacity?.gt,
          capacity_lte: query?.capacity?.lte,
          capacity_gte: query?.capacity?.gte,
          total_registrations_lt: query?.spots_filled?.lt,
          total_registrations_gt: query?.spots_filled?.gt,
          total_registrations_lte: query?.spots_filled?.lte,
          total_registrations_gte: query?.spots_filled?.gte,
          scheduled_lt:
            query?.scheduled?.lt &&
            !query?.scheduled_type?.includes('is upcoming') &&
            !query?.scheduled_type?.includes('is past')
              ? toDate(query?.scheduled?.lt)
              : undefined,
          scheduled_gt:
            query?.scheduled?.gt &&
            !query?.scheduled_type?.includes('is upcoming') &&
            !query?.scheduled_type?.includes('is past')
              ? toDate(query?.scheduled?.gt)
              : undefined,
          scheduled_lte: query?.scheduled_type?.includes('is past')
            ? DateTime.now().toJSDate()
            : query?.scheduled?.lte
            ? toDate(query?.scheduled?.lte)
            : undefined,
          scheduled_gte: query?.scheduled_type?.includes('is upcoming')
            ? DateTime.now().toJSDate()
            : query?.scheduled?.gte
            ? toDate(query?.scheduled?.gte)
            : undefined,
        },
        { signal },
      )
    },
  })
  const {
    handlePublish,
    handleBulkPublish,
    handleUnpublish,
    handleBulkUnpublish,
    handleCancel,
    handleBulkCancel,
    handleDelete,
    handleBulkDelete,
  } = useUpdateEvents({ programId: props.programId, search, filter: fetchProps.query })

  const { handleCreateFeedback } = useCreateFeedback({ programId: props.programId })

  return {
    data,
    fetchProps,
    error: error || undefined,
    isError,
    isLoading,
    pagination,
    search,
    setFetchProps,
    setSearch,
    setPagination,
    handlePublish,
    handleBulkPublish,
    handleUnpublish,
    handleBulkUnpublish,
    handleCancel,
    handleBulkCancel,
    handleDelete,
    handleBulkDelete,
    handleCreateFeedback,
  }
}

interface FetchInfiniteEvent extends Omit<FetchEvents, 'setFetchProps' | 'setPagination'> {
  isFetching: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  fetchNextPage: (
    options?: FetchNextPageOptions | undefined,
  ) => Promise<InfiniteQueryObserverResult<ProgramEventList, ErrorResponse>>
}

interface IInfiniteProps extends IProps {
  fetchAll?: boolean
}

export const useFetchInfiniteEvents = (props: IInfiniteProps): FetchInfiniteEvent => {
  const {
    data,
    error,
    fetchProps,
    isError,
    isLoading,
    pagination,
    search: _search,
    setSearch,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage = false,
  } = useFetchInfiniteMany<ProgramEvent, EventFilterProps>({
    type: 'program_event',
    programId: props.programId,
    search: props.search,
    fetchProps: props.fetchProps || DefaultEventFetchProps,
    pagination: props.pagination || DefaultDetailsPagination,
    fetchAll: props.fetchAll,
    isEnabled: props.isEnabled,
    queryFn: async (queryParams): Promise<ProgramEventList> => {
      const { programId, limit, skip, query, sort, order, search, signal } = queryParams
      const client = new EventsApi(generateApiConfig())
      return await client.findProgramEvents(
        {
          program_id: programId,
          limit,
          skip,
          order,
          sort,
          search,
          age_lt: query?.age?.lt,
          age_gt: query?.age?.gt,
          age_lte: query?.age?.lte,
          age_gte: query?.age?.gte,
          gender: query?.gender && query?.gender.length > 0 ? query.gender : undefined,
          venue_city:
            query?.venue_city && query?.venue_city.length > 0 ? query.venue_city : undefined,
          online_event:
            typeof query?.online_event !== 'undefined' ? query?.online_event : undefined,
          status: query?.status && query?.status.length > 0 ? query.status : undefined,
          type: query?.type && query?.type.length > 0 ? query.type : undefined,
          tags: query?.tags && query?.tags.length > 0 ? query.tags : undefined,
          id: query?.id && query?.id.length > 0 ? query.id : undefined,
          exclude_id:
            query?.exclude_id && query?.exclude_id.length > 0 ? query.exclude_id : undefined,
          is_appointment:
            typeof query?.is_appointment !== 'undefined' ? query.is_appointment : undefined,
          capacity_lt: query?.capacity?.lt,
          capacity_gt: query?.capacity?.gt,
          capacity_lte: query?.capacity?.lte,
          capacity_gte: query?.capacity?.gte,
          total_registrations_lt: query?.spots_filled?.lt,
          total_registrations_gt: query?.spots_filled?.gt,
          total_registrations_lte: query?.spots_filled?.lte,
          total_registrations_gte: query?.spots_filled?.gte,
          scheduled_lt:
            query?.scheduled?.lt &&
            !query?.scheduled_type?.includes('is upcoming') &&
            !query?.scheduled_type?.includes('is past')
              ? toDate(query?.scheduled?.lt)
              : undefined,
          scheduled_gt:
            query?.scheduled?.gt &&
            !query?.scheduled_type?.includes('is upcoming') &&
            !query?.scheduled_type?.includes('is past')
              ? toDate(query?.scheduled?.gt)
              : undefined,
          scheduled_lte: query?.scheduled_type?.includes('is past')
            ? DateTime.now().toJSDate()
            : query?.scheduled?.lte
            ? toDate(query?.scheduled?.lte)
            : undefined,
          scheduled_gte: query?.scheduled_type?.includes('is upcoming')
            ? DateTime.now().toJSDate()
            : query?.scheduled?.gte
            ? toDate(query?.scheduled?.gte)
            : undefined,
        },
        { signal },
      )
    },
  })
  const {
    handlePublish,
    handleBulkPublish,
    handleUnpublish,
    handleBulkUnpublish,
    handleCancel,
    handleBulkCancel,
    handleDelete,
    handleBulkDelete,
  } = useUpdateEvents({ programId: props.programId, search: _search, filter: fetchProps.query })
  const { handleCreateFeedback } = useCreateFeedback({ programId: props.programId })
  return {
    data,
    error,
    fetchProps,
    hasNextPage,
    isError,
    isFetching,
    isFetchingNextPage,
    isLoading,
    pagination,
    search: _search,
    fetchNextPage,
    handlePublish,
    handleBulkPublish,
    handleUnpublish,
    handleBulkUnpublish,
    handleCancel,
    handleBulkCancel,
    handleDelete,
    handleBulkDelete,
    handleCreateFeedback,
    setSearch,
  }
}
